import { usePrivy } from '@privy-io/react-auth';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useCurrentUser, userQueryKeys } from '@/hooks/api/useUserQueries';
import { useQueryClient } from '@tanstack/react-query';

export function useAuth() {
  const queryClient = useQueryClient();
  const {
    ready: privyReady,
    authenticated: privyAuthenticated,
    user: privyUser,
    logout: privyLogout,
  } = usePrivy();


  const {
    user,
    isAuthenticated,
    isLoading: storeLoading,
    error: storeError,
    setUser,
    setLoading,
    setError,
    logout: storeLogout,
    hasPermission,
    hasRole,
    canAccessOrganization,
  } = useAuthStore();

  // Use React Query to fetch current user data
  const { 
    data: currentUserData, 
    isLoading: queryLoading, 
    error: queryError,
    isError: queryIsError,
  } = useCurrentUser();



  // Sync React Query data with Zustand store
  useEffect(() => {
    if (currentUserData) {
      setUser(currentUserData);
    }
  }, [currentUserData, setUser]);

  // Clear user when Privy is not authenticated
  useEffect(() => {
    if (privyReady && !privyAuthenticated) {
      setUser(null);
    }
  }, [privyReady, privyAuthenticated, setUser]);

  // Sync authentication state with Privy
  useEffect(() => {
    if (!privyReady) {
      setLoading(true);
      return;
    }

    if (!privyAuthenticated || !privyUser) {
      setUser(null);
      setLoading(false);
      return;
    }

    // Loading state is managed by React Query when authenticated
    if (privyAuthenticated && !queryLoading) {
      setLoading(false);
    }
  }, [privyReady, privyAuthenticated, privyUser, queryLoading, setUser, setLoading]);

  // Handle query errors
  useEffect(() => {
    if (queryIsError && queryError) {
      const errorMessage = queryError instanceof Error 
        ? queryError.message 
        : 'Failed to load user information';
      setError(errorMessage);
    } else {
      setError(null);
    }
  }, [queryIsError, queryError, setError]);


  const handleLogout = async () => {
    try {
      await privyLogout();
      storeLogout();
      // Clear the query cache on logout
      queryClient.removeQueries({ queryKey: userQueryKeys.current() });
      // Don't redirect here - let AuthGuard handle it
    } catch (error) {
      console.error('Logout failed:', error);
      setError('Failed to logout');
    }
  };

  return {
    // Authentication state
    user,
    isAuthenticated,
    isLoading: storeLoading || queryLoading || !privyReady,
    error: storeError || (queryIsError ? 'Failed to load user information' : null),

    // Permission methods
    hasPermission,
    hasRole,
    canAccessOrganization,

    // Actions
    logout: handleLogout,
  };
}
